import React from 'react';
import { LuArrowLeft, LuChevronRight } from 'react-icons/lu';
import { Link } from 'react-router-dom';

import {
    CmsContentWorkflowStatus,
    CmsHistoryView,
    CmsMicrositePublicStatus,
    CmsRenderArtifactStatus,
    CmsRenderArtifactView,
} from '@ndvr/cms-api';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDateTime, formatRelativeDateTime, parseServerDateTime } from 'utils';
import { Tooltip } from 'widgets';

export const CmsPageHeader = ({
    title,
    eyebrow,
    actions,
    parent,
}: {
    title: string;
    eyebrow?: string;
    actions?: React.ReactNode;
    parent?: {
        label: string;
        to: string;
    };
}) => (
    <div className="flex flex-wrap items-start justify-between gap-4 border-b bg-white px-8 py-4">
        <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-3">
                {parent && (
                    <>
                        <Link
                            className="text-muted-foreground hover:text-foreground focus:ring-ring inline-flex shrink-0 items-center gap-1.5 rounded-sm text-sm font-medium transition focus:ring-2 focus:outline-none"
                            to={parent.to}
                        >
                            <LuArrowLeft className="size-4" />
                            {parent.label}
                        </Link>
                        <LuChevronRight className="text-muted-foreground size-4 shrink-0" />
                    </>
                )}
                <h2 className="text-cocoa-1000 truncate text-xl font-medium">{title}</h2>
            </div>
            {eyebrow && <p className="text-muted-foreground mt-1 text-sm">{eyebrow}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
);

export const CmsStatusBadge = ({ status }: { status?: string }) => {
    const normalized = status ?? 'UNKNOWN';
    const successfulStatuses = new Set<string>([
        CmsMicrositePublicStatus.PUBLISHED,
        CmsContentWorkflowStatus.APPROVED,
        CmsRenderArtifactStatus.SUCCEEDED,
        'Live',
    ]);
    const failedStatuses = new Set<string>([CmsRenderArtifactStatus.FAILED, 'Publish failed']);
    const variant = successfulStatuses.has(normalized)
        ? 'success'
        : failedStatuses.has(normalized)
          ? 'destructive'
          : 'secondary';

    return <Badge variant={variant}>{normalized.replaceAll('_', ' ')}</Badge>;
};

export const CmsShell = ({ children }: React.PropsWithChildren) => (
    <div className="flex min-h-[calc(100vh-120px)] flex-col bg-white">{children}</div>
);

export const CmsSectionCard = ({
    title,
    children,
    actions,
}: React.PropsWithChildren<{ title: string; actions?: React.ReactNode }>) => (
    <Card className="rounded-md">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
            <CardTitle className="text-base">{title}</CardTitle>
            {actions}
        </CardHeader>
        <CardContent className="space-y-4">{children}</CardContent>
    </Card>
);

export const CmsFieldErrorList = ({ errors }: { errors?: string[] }) => {
    if (!errors?.length) {
        return null;
    }

    return (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {errors.map((error) => (
                <p key={error}>{error}</p>
            ))}
        </div>
    );
};

export const CmsArtifactSummary = ({ artifact }: { artifact?: CmsRenderArtifactView }) => {
    if (!artifact) {
        return <p className="text-muted-foreground text-sm">No render artifact is available.</p>;
    }

    return (
        <dl className="grid gap-2 text-sm md:grid-cols-2">
            <div>
                <dt className="text-muted-foreground">Artifact</dt>
                <dd className="break-all">{artifact.cmsRenderArtifactGUID}</dd>
            </div>
            <div>
                <dt className="text-muted-foreground">Status</dt>
                <dd>
                    <CmsStatusBadge status={artifact.status} />
                </dd>
            </div>
            <div>
                <dt className="text-muted-foreground">Renderer deploy</dt>
                <dd className="break-all">{artifact.rendererDeployReference}</dd>
            </div>
            <div>
                <dt className="text-muted-foreground">Storage prefix</dt>
                <dd className="break-all">{artifact.storagePrefix}</dd>
            </div>
            {artifact.failureMessage && (
                <div className="md:col-span-2">
                    <dt className="text-muted-foreground">Failure</dt>
                    <dd className="text-red-700">{artifact.failureMessage}</dd>
                </div>
            )}
        </dl>
    );
};

export const latestWorkflowByVersion = (history?: CmsHistoryView) => {
    const result = new Map<string, CmsContentWorkflowStatus>();
    history?.workflowEvents.forEach((event) => result.set(event.cmsContentVersionGUID, event.status));
    return result;
};

export const CmsRelativeDateTime = ({ value, fallback = 'Never' }: { value?: string; fallback?: string }) => {
    if (!value) {
        return <>{fallback}</>;
    }

    const label = formatRelativeDateTime(value);
    const date = parseServerDateTime(value);
    const exactDate = formatDateTime(date);

    return (
        <Tooltip tooltip={exactDate}>
            <time dateTime={value}>{label}</time>
        </Tooltip>
    );
};
